<script setup>
import {
	ref,
	onMounted,
	onUnmounted,
} from 'vue'
import { HTTP } from '@/services/http'
import { traverse } from '@/modules/util/object'

const items = ref([])
const displayItems = ref([])

let timeout = null

const deleteRequest = async (uid) => {
	const item = await HTTP.cache.get(uid, true)
	if (!item?.value?.request)
		return

	let vehicle, inspection
	const body = JSON.parse(item.value.request.body)
	if (body.vehicle)
		vehicle = JSON.parse(body.vehicle)
	if (body.inspection)
		inspection = JSON.parse(body.inspection)

	const uids = []

	const search = (key, val, parent) => {
		if (val?.uid)
			uids.push(val.uid)
	}

	await traverse(vehicle || {}, search)
	await traverse(inspection || {}, search)

	if (HTTP.deleteCached(uid)) {
		uids.forEach(HTTP.deleteCached)
		displayItems.value = displayItems.value.filter(i => i.uid !== uid)
	}
}

/**
 * Let's not display sensitive data in the UI
 */
const sanitize = async (item) => {
	const displayItem = JSON.parse(JSON.stringify(item))
	if (!displayItem.value.request.body) {
		return displayItem
	}
    
	const body = JSON.parse(displayItem.value.request.body)

	Object.keys(body).forEach(key => {
		try {
			body[key] = JSON.parse(body[key])
		} catch (err) {
			return
		}
	})

	const blacklist = {
		'username': true,
		'usertoken': true,
		'password': true,
		'newpassword': true,
		'token': true,
		'secret': true,
		'signature': true,
		'signature_email': true,
	}

	const important = {
		'motorvehicle_id': true,
		'motorvehicleinspection_id': true,
		'inspectiontype_id': true,
		'id': true,
	}

	await traverse(body, (key, value, previous, path) => {
		if (blacklist[key]) {
			previous[key] = '********'
			return false
		}

		if (important[key]) {
			displayItem.value.request[path ? `${path}.${key}` : key] = value
			delete displayItem.value.request[key]
			return false
		}

		if (key === 'uid') {
			displayItem.value.request[path] = 'Image awaiting upload'
			return false
		}
        
		if (key === 'imagename') {
			displayItem.value.request[path] = `Image uploaded: ${value}`
			return false
		}

		return true
	})

	displayItem.value.request.body = body

	return displayItem
}

const refresh = async () => {
	items.value = (await HTTP.cache.all()).filter(item => {
		return item.value?.path !== '/uploadinspectionimage'
	})

	displayItems.value = await Promise.all(items.value.map(sanitize))

	timeout = setTimeout(refresh, 1000)
}

onMounted(async () => {
	await refresh()
})
onUnmounted(() => {
	if (timeout) {
		clearTimeout(timeout)
	}
})
</script>

<template>
<v-row class="ma-4">
	<v-alert v-if="!displayItems.length">
		No pending network requests to display
	</v-alert>
	<v-col
		v-for="item in displayItems"
		:key="item.uid"
		cols="12"
		md="6"
	>
		<v-card>
			<v-card-title>{{ new Date(item.timestamp).toLocaleString() }} - {{ item.value.path }}</v-card-title>
			<v-card-text>
				<v-alert
					v-for="key, i in Object.keys(item.value.request)"
					:key="i"
					class="pa-2 mb-2"
				>
					<strong>{{ key }}</strong>: {{ item.value.request[key] }}
				</v-alert>
			</v-card-text>
			<v-card-actions>
				<v-btn
					color="error"
					@click="deleteRequest(item.uid)"
				>
					Delete
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-col>
</v-row>
</template>

<style lang="scss" scoped>

</style>